import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  Timestamp,
  where
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/hotel_logo.png';
import { HOTEL_NAME } from "../Variables.js";

Modal.setAppElement('#root');

const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const naturalSort = (a, b) => {
  const codeA = String(a.code ?? '').trim();
  const codeB = String(b.code ?? '').trim();
  return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
};

const RoomTypes = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [roomTypes, setRoomTypes] = useState([]);
  const [filteredRoomTypes, setFilteredRoomTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [amenities, setAmenities] = useState([]); // All amenities from collection
  const [ratePlans, setRatePlans] = useState([]); // All rate plans from collection

  const [newRoomType, setNewRoomType] = useState({
    name: '',
    code: '',
    maxOccupancy: 2,
    baseRate: 0,
    description: '',
    amenityIds: [],
    ratePlanIds: [], // ← New: selected rate plan IDs
    active: true
  });

  const [editingRoomType, setEditingRoomType] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [currentRoomType, setCurrentRoomType] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('excel');

  const roomTypesCollectionRef = collection(db, "roomTypes");
  const amenitiesCollectionRef = collection(db, "amenities");
  const ratePlansCollectionRef = collection(db, "ratePlans");
  const storeName = `${HOTEL_NAME}`;

  useEffect(() => {
    if (user && userData) {
      fetchRoomTypes();
      fetchAmenities();
      fetchRatePlans();
    }
  }, [user, userData]);

  useEffect(() => {
    const filtered = roomTypes.filter((rt) =>
      rt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (rt.code && rt.code.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredRoomTypes(filtered);
  }, [searchTerm, roomTypes]);

  const fetchAmenities = async () => {
    try {
      const q = query(amenitiesCollectionRef);
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAmenities(list);
    } catch (err) {
      console.error("Error fetching amenities:", err);
    }
  };

  const fetchRatePlans = async () => {
    try {
      const q = query(ratePlansCollectionRef, where("active", "==", true));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      list.sort((a, b) => a.name.localeCompare(b.name));
      setRatePlans(list);
    } catch (err) {
      console.error("Error fetching rate plans:", err);
    }
  };

  const fetchRoomTypes = async () => {
    if (!user?.uid) return;

    setIsFetching(true);
    try {
      const q = query(roomTypesCollectionRef);
      const data = await getDocs(q);
      let list = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

      // Enrich with amenity names and rate plan names
      const enriched = await Promise.all(list.map(async (rt) => {
        let amenitiesDisplay = 'None';
        let ratePlansDisplay = 'None';

        if (rt.amenityIds && rt.amenityIds.length > 0) {
          const amenityQueries = rt.amenityIds.map(id =>
            getDocs(query(amenitiesCollectionRef, where("__name__", "==", id)))
          );
          const snapshots = await Promise.all(amenityQueries);
          amenitiesDisplay = snapshots
            .flatMap(snap => snap.docs)
            .map(doc => doc.data().name)
            .filter(Boolean)
            .join(', ') || 'None';
        }

        if (rt.ratePlanIds && rt.ratePlanIds.length > 0) {
          const ratePlanQueries = rt.ratePlanIds.map(id =>
            getDocs(query(ratePlansCollectionRef, where("__name__", "==", id)))
          );
          const snapshots = await Promise.all(ratePlanQueries);
          ratePlansDisplay = snapshots
            .flatMap(snap => snap.docs)
            .map(doc => doc.data().name)
            .filter(Boolean)
            .join(', ') || 'None';
        }

        return {
          ...rt,
          amenitiesDisplay,
          ratePlansDisplay
        };
      }));

      setRoomTypes(enriched);
      setHasFetched(true);
    } catch (err) {
      console.error("Error fetching room types:", err);
      setError("Failed to fetch room types");
      setShowModal(true);
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRoomType(prev => ({
      ...prev,
      [name]: name === 'maxOccupancy' || name === 'baseRate' ? Number(value) : value
    }));
  };

  const handleAmenityToggle = (amenityId) => {
    setNewRoomType(prev => {
      const ids = prev.amenityIds || [];
      if (ids.includes(amenityId)) {
        return { ...prev, amenityIds: ids.filter(id => id !== amenityId) };
      } else {
        return { ...prev, amenityIds: [...ids, amenityId] };
      }
    });
  };

  const handleRatePlanToggle = (ratePlanId) => {
    setNewRoomType(prev => {
      const ids = prev.ratePlanIds || [];
      if (ids.includes(ratePlanId)) {
        return { ...prev, ratePlanIds: ids.filter(id => id !== ratePlanId) };
      } else {
        return { ...prev, ratePlanIds: [...ids, ratePlanId] };
      }
    });
  };

  const handleAddRoomType = async () => {
    if (!newRoomType.name.trim()) {
      setError("Room type name cannot be empty");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(roomTypesCollectionRef, {
        ...newRoomType,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Room type added successfully");
      fetchRoomTypes();
      resetForm();
      setIsAddModalOpen(false);
    } catch (err) {
      setError("Failed to add room type");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateRoomType = async () => {
    setUpdate(true);
    try {
      const roomTypeDoc = doc(db, "roomTypes", editingRoomType.id);
      await updateDoc(roomTypeDoc, {
        ...newRoomType,
        baseRate: Number(newRoomType.baseRate),
        maxOccupancy: Number(newRoomType.maxOccupancy)
      });
      setSuccessMessage("Room type updated successfully");
      fetchRoomTypes();
      resetForm();
      setEditingRoomType(null);
      setIsEditModalOpen(false);
    } catch (err) {
      setError("Failed to update room type");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetForm = () => {
    setNewRoomType({
      name: '',
      code: '',
      maxOccupancy: 2,
      baseRate: 0,
      description: '',
      amenityIds: [],
      ratePlanIds: [],
      active: true
    });
  };

  const handleEdit = (rt) => {
    setEditingRoomType(rt);
    setNewRoomType({
      name: rt.name,
      code: rt.code || '',
      maxOccupancy: rt.maxOccupancy || 2,
      baseRate: rt.baseRate || 0,
      description: rt.description || '',
      amenityIds: rt.amenityIds || [],
      ratePlanIds: rt.ratePlanIds || [],
      active: rt.active ?? true
    });
    setIsEditModalOpen(true);
  };

  const handleFileChange = (e) => setFile(e.target.files[0]);
  const handleFileTypeChange = (e) => setFileType(e.target.value);

  const processBulkImport = async () => {
    if (!file) {
      setError("Please select a file first");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      let dataArray = [];

      if (fileType === 'excel') {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer);
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        dataArray = XLSX.utils.sheet_to_json(sheet);
      } else {
        const text = await file.text();
        const result = Papa.parse(text, { header: true, skipEmptyLines: true });
        dataArray = result.data;
      }

      if (dataArray.length === 0) throw new Error("No data found in file");

      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < dataArray.length; i++) {
        try {
          const rt = dataArray[i];
          if (!rt.name) { errorCount++; continue; }

          await addDoc(roomTypesCollectionRef, {
            name: rt.name.trim(),
            code: String(rt.code ?? '').trim(),
            maxOccupancy: Number(rt.maxOccupancy) || 2,
            baseRate: Number(rt.baseRate) || 0,
            description: rt.description || '',
            active: rt.active !== undefined ? Boolean(rt.active) : true,
            userId: user?.uid,
            createdAt: Timestamp.now()
          });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Error importing row ${i + 1}:`, err);
        }
      }

      setSuccessMessage(`Bulk import completed: ${successCount} room types added`);
      fetchRoomTypes();
    } catch (err) {
      setError(`Bulk import failed: ${err.message}`);
    } finally {
      setUpdate(false);
      setFile(null);
      setIsBulkImportModalOpen(false);
      setShowModal(true);
    }
  };

  const generateRoomTypesPDF = () => {
    if (roomTypes.length === 0) {
      setError("No room types available to generate PDF");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let y = 10;
    doc.addImage(Logo, "PNG", 14, y, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, 35, y + 5);
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text("Room Types Report", 35, y + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, y + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, y + 23);
    y += 28;

    const sorted = [...roomTypes].sort(naturalSort);
    const tableData = sorted.map((rt, idx) => [
      (idx + 1).toString(),
      rt.code || '-',
      rt.name,
      rt.maxOccupancy,
      `₦${rt.baseRate.toLocaleString()}`,
      rt.amenitiesDisplay || 'None',
      rt.ratePlansDisplay || 'None',
      rt.active ? 'Active' : 'Inactive',
      rt.createdAt ? dateFormatter.format(rt.createdAt.toDate()) : '-'
    ]);

    autoTable(doc, {
      startY: y,
      head: [['S/N', 'Code', 'Room Type', 'Max Occupancy', 'Base Rate', 'Amenities', 'Rate Plans', 'Status', 'Created']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 18 },
        2: { cellWidth: 35 },
        3: { cellWidth: 18 },
        4: { cellWidth: 22 },
        5: { cellWidth: 35 },
        6: { cellWidth: 35 },
        7: { cellWidth: 18 },
        8: { cellWidth: 25 }
      }
    });

    doc.save(`room_types_${formatDateForFilename(new Date())}.pdf`);
  };

  const confirmDeactivate = (rt) => {
    setCurrentRoomType(rt);
    setIsDeactivateModalOpen(true);
  };

  const handleDeactivate = async (id) => {
    setUpdate(true);
    try {
      const ref = doc(db, "roomTypes", id);
      await updateDoc(ref, { active: false });
      setSuccessMessage("Room type deactivated successfully");
      fetchRoomTypes();
    } catch (err) {
      setError("Failed to deactivate room type");
    } finally {
      setUpdate(false);
      setIsDeactivateModalOpen(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Cashier', 'Admin', 'Supreme', 'Front Desk'];

  const displayContent = () => {
    if (isFetching && !hasFetched) {
      return <p className="loading-text">Loading room types ...</p>;
    }

    if (filteredRoomTypes.length === 0) {
      return searchTerm
        ? <p className="loading-text">No room types match your search.</p>
        : <p className="loading-text">No room types found. Add your first room type or import in bulk.</p>;
    }

    return filteredRoomTypes.map((rt) => (
      <div key={rt.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{rt.name}</strong><br />
            <span>Code: {rt.code || '—'}</span>&nbsp;-&nbsp;
            <span>Max: {rt.maxOccupancy} guests</span><br />
            <span>Base Rate: ₦{rt.baseRate.toLocaleString()}</span><br />
            <span>Amenities: {rt.amenitiesDisplay || 'None'}</span><br />
            <span>Rate Plans: {rt.ratePlansDisplay || 'None'}</span><br />
            <span className="size5 font-gold bold">{rt.active ? 'Active' : 'Not active'}</span>
          </p>
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEdit(rt)}>
            <i className="fas fa-edit"></i>
          </button>
          {rt.active && (
            <button className="list-btn delete-btn" onClick={() => confirmDeactivate(rt)}>
              <i className="fas fa-ban"></i>
            </button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Room Types</h2>
            <Link to="#" className="home-link" onClick={() => fetchRoomTypes()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed4">
            <div className="row justify-center align-center gap-15 add-buttons add-buttons1 margin-top-cashflow1">
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Room Type</button>
              {/* <button className="list-btn" onClick={generateRoomTypesPDF}><i className="fas fa-print"></i></button>
              <button className="login-button login-button-d caps" onClick={() => setIsBulkImportModalOpen(true)}>Bulk Import</button> */}
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name or code"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Modal */}
      <Modal isOpen={isAddModalOpen} onRequestClose={() => { setIsAddModalOpen(false); resetForm(); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Add Room Type</h2>
        <form className="session-form modal-scroll">
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Name</label>
              <input type="text" name="name" className="input-field" placeholder="e.g., Standard, Deluxe Suite" value={newRoomType.name} onChange={handleInputChange} required />
            </div>
            <div>
              <label className="input-label">Code (Optional)</label>
              <input type="text" name="code" className="input-field" placeholder="e.g., STD, DLX" value={newRoomType.code} onChange={handleInputChange} />
            </div>
          </div>

          <div className="desktop-form-input">
            <div>
              <label className="input-label">Max Occupancy</label>
              <input type="number" name="maxOccupancy" className="input-field" min="1" value={newRoomType.maxOccupancy} onChange={handleInputChange} />
            </div>
            <div>
              <label className="input-label">Base Rate (₦)</label>
              <input type="number" name="baseRate" className="input-field" min="0" step="1000" value={newRoomType.baseRate} onChange={handleInputChange} />
            </div>
          </div>
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Amenities</label>
              <div className="column gap-05" style={{maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #464646', borderRadius: '4px', backgroundColor: '#181818'}}>
                {amenities.length === 0 ? (
                  <p>No amenities defined yet. <Link to="/amenities">Add some first</Link></p>
                ) : (
                  amenities
                    .filter(a => a.active !== false)
                    .map(amenity => (
                      <label key={amenity.id} style={{display: 'block', padding: '4px 0'}}>
                        <input
                          type="checkbox"
                          className='gold-tick'
                          checked={newRoomType.amenityIds.includes(amenity.id)}
                          onChange={() => handleAmenityToggle(amenity.id)}
                        />
                        {' '}{amenity.name}
                        {amenity.price > 0 && <span className="font-gold"> (₦{amenity.price.toLocaleString()})</span>}
                        {amenity.type !== 'Complimentary' && <span className="size5"> - {amenity.type}</span>}
                      </label>
                    ))
                )}
              </div>
            </div>
            <div>
              <label className="input-label">Rate Plans</label>
              <div className="column gap-05" style={{maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #464646', borderRadius: '4px', backgroundColor: '#181818'}}>
                {ratePlans.length === 0 ? (
                  <p>No rate plans defined yet. <Link to="/rate-plans">Add some first</Link></p>
                ) : (
                  ratePlans.map(plan => (
                    <label key={plan.id} style={{display: 'block', padding: '4px 0'}}>
                      <input
                        type="checkbox"
                        className='gold-tick'
                        checked={newRoomType.ratePlanIds.includes(plan.id)}
                        onChange={() => handleRatePlanToggle(plan.id)}
                      />
                      {' '}{plan.name}
                      {plan.discountPercentage > 0 && <span className="font-gold"> ({plan.discountPercentage}% off)</span>}
                      {plan.includesBreakfast && <span className="size5"> - Breakfast Included</span>}
                      {plan.refundable === false && <span className="size5" style={{color: '#ff4444'}}> - Non-refundable</span>}
                    </label>
                  ))
                )}
              </div>
            </div>
          </div>
          <label className="input-label">Description (Optional)</label>
          <textarea name="description" className="input-field" rows="3" value={newRoomType.description} onChange={handleInputChange} />

          <div className="row align-center gap-1">
            <button type="button" className="login-button" onClick={() => { setIsAddModalOpen(false); resetForm(); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleAddRoomType}>Add Room Type</button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onRequestClose={() => { setIsEditModalOpen(false); resetForm(); setEditingRoomType(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Edit Room Type</h2>
        <form className="session-form modal-scroll">
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Name</label>
              <input type="text" name="name" className="input-field" value={newRoomType.name} onChange={handleInputChange} required />
            </div>
            <div>
              <label className="input-label">Code</label>
              <input type="text" name="code" className="input-field" value={newRoomType.code} onChange={handleInputChange} />
            </div>
          </div>

          <div className="desktop-form-input">
            <div>
              <label className="input-label">Max Occupancy</label>
              <input type="number" name="maxOccupancy" className="input-field" min="1" value={newRoomType.maxOccupancy} onChange={handleInputChange} />
            </div>
            <div>
              <label className="input-label">Base Rate (₦)</label>
              <input type="number" name="baseRate" className="input-field" min="0" value={newRoomType.baseRate} onChange={handleInputChange} />
            </div>
          </div>
          <div className="desktop-form-input">
            <div>
              <label className="input-label">Amenities</label>
              <div className="column gap-05" style={{maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #464646', borderRadius: '4px', backgroundColor: '#181818'}}>
                {amenities
                  .filter(a => a.active !== false)
                  .map(amenity => (
                    <label key={amenity.id} style={{display: 'block', padding: '4px 0'}}>
                      <input
                        type="checkbox"
                        className='gold-tick'
                        checked={newRoomType.amenityIds.includes(amenity.id)}
                        onChange={() => handleAmenityToggle(amenity.id)}
                      />
                      {' '}{amenity.name}
                      {amenity.price > 0 && <span className="font-gold"> (₦{amenity.price.toLocaleString()})</span>}
                      {amenity.type !== 'Complimentary' && <span className="size5"> - {amenity.type}</span>}
                    </label>
                  ))}
              </div>
            </div>
            <div>
              <label className="input-label">Rate Plans</label>
              <div className="column gap-05" style={{maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #464646', borderRadius: '4px', backgroundColor: '#181818'}}>
                {ratePlans.length === 0 ? (
                  <p>No rate plans defined yet.</p>
                ) : (
                  ratePlans.map(plan => (
                    <label key={plan.id} style={{display: 'block', padding: '4px 0'}}>
                      <input
                        type="checkbox"
                        className='gold-tick'
                        checked={newRoomType.ratePlanIds.includes(plan.id)}
                        onChange={() => handleRatePlanToggle(plan.id)}
                      />
                      {' '}{plan.name}
                      {plan.discountPercentage > 0 && <span className="font-gold"> ({plan.discountPercentage}% off)</span>}
                      {plan.includesBreakfast && <span className="size5"> - Breakfast Included</span>}
                      {plan.refundable === false && <span className="size5" style={{color: '#ff4444'}}> - Non-refundable</span>}
                    </label>
                  ))
                )}
                </div>
              </div>
            </div>
          <label className="input-label">Description</label>
          <textarea name="description" className="input-field" rows="3" value={newRoomType.description} onChange={handleInputChange} />

          <label>
            <input type="checkbox" className='gold-tick' checked={newRoomType.active} onChange={(e) => setNewRoomType({ ...newRoomType, active: e.target.checked })} />
            Active
          </label>

          <div className="row align-center justify-center gap-1">
            <button type="button" className="login-button" onClick={() => { setIsEditModalOpen(false); resetForm(); setEditingRoomType(null); }}>Cancel</button>
            <button type="button" className="login-button" onClick={handleUpdateRoomType}>Update Room Type</button>
          </div>
        </form>
      </Modal>

      {/* Deactivate Confirmation Modal */}
      <Modal isOpen={isDeactivateModalOpen} onRequestClose={() => setIsDeactivateModalOpen(false)} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Deactivate Room Type</h2>
        {currentRoomType && (
          <div>
            <p>Are you sure you want to deactivate <strong>{currentRoomType.name}</strong>?</p>
            <div className="row align-center gap-1">
              <button className="login-button" onClick={() => setIsDeactivateModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" onClick={() => handleDeactivate(currentRoomType.id)}>Deactivate</button>
            </div>
          </div>
        )}
      </Modal>

      {/* Bulk Import Modal */}
      <Modal isOpen={isBulkImportModalOpen} onRequestClose={() => { setIsBulkImportModalOpen(false); setFile(null); }} className="custom-modal" overlayClassName="custom-modal-overlay">
        <h2>Bulk Import Room Types</h2>
        <div className="modal-form">
          <div style={{ marginBottom: '5px' }}>
            <label><input type="radio" name="fileType" value="excel" checked={fileType === 'excel'} onChange={handleFileTypeChange} /> Excel (.xlsx)</label>&nbsp;&nbsp;
            <label><input type="radio" name="fileType" value="csv" checked={fileType === 'csv'} onChange={handleFileTypeChange} /> CSV (.csv)</label>
          </div>

          <input type="file" accept={fileType === 'excel' ? '.xlsx,.xls' : '.csv'} onChange={handleFileChange} className="input-field1" />

          <div className="row align-center justify-center gap-15">
            <button className="login-button caps" onClick={() => { setIsBulkImportModalOpen(false); setFile(null); }}>Cancel</button>
            <button className="login-button caps" onClick={processBulkImport} disabled={!file || update}>Import</button>
          </div>

          <div className="import-instructions">
            <h4>File Format Instructions:</h4>
            <p><strong>REQUIRED:</strong> name</p>
            <p><strong>OPTIONAL:</strong> code, maxOccupancy, baseRate, description, active</p>
            <p>Note: Amenities and Rate Plans are to be selected in the app.</p>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default RoomTypes;